import './bootstrap';
import '../css/app.css';

import { createApp, h } from 'vue';
import { createInertiaApp, router as inertiaRouter } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createPinia } from 'pinia';

const appName = import.meta.env.VITE_APP_NAME || 'WokiTrack';

const getRouteManifest = () => {
    if (!window.__routeManifest || typeof window.__routeManifest !== 'object') {
        return {};
    }

    return window.__routeManifest;
};

const toQueryString = (query) => {
    const entries = [];

    Object.entries(query).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null && item !== '') {
                    entries.push([`${key}[]`, String(item)]);
                }
            });
            return;
        }

        entries.push([key, String(value)]);
    });

    return new URLSearchParams(entries).toString();
};

window.route = function (name, params) {
    const manifest = getRouteManifest();
    const template = manifest[name];

    if (!template || typeof template !== 'string') {
        return '#';
    }

    const isObjectParams = params !== null && typeof params === 'object' && !Array.isArray(params);
    const namedParams = isObjectParams ? { ...params } : {};
    const orderedParams = Array.isArray(params)
        ? [...params]
        : (!isObjectParams && params !== undefined && params !== null ? [params] : []);

    let unresolvedRequired = false;
    let orderedIndex = 0;
    const usedNamed = new Set();

    let path = template.replace(/\{([^}]+)\}/g, (_, rawKey) => {
        const optional = rawKey.endsWith('?');
        const key = optional ? rawKey.slice(0, -1) : rawKey;
        const keyBase = key.includes(':') ? key.split(':')[0] : key;

        let value;
        if (Object.prototype.hasOwnProperty.call(namedParams, keyBase)) {
            value = namedParams[keyBase];
            usedNamed.add(keyBase);
        } else if (Object.prototype.hasOwnProperty.call(namedParams, key)) {
            value = namedParams[key];
            usedNamed.add(key);
        } else if (orderedIndex < orderedParams.length) {
            value = orderedParams[orderedIndex++];
        }

        if (value === undefined || value === null || value === '') {
            if (optional) {
                return '__OPTIONAL__';
            }

            unresolvedRequired = true;
            return '';
        }

        return encodeURIComponent(String(value));
    });

    path = path.replace(/\/__OPTIONAL__/g, '');
    path = path.replace(/__OPTIONAL__/g, '');
    path = path.replace(/\/+/g, '/');

    if (!path.startsWith('/')) {
        path = `/${path}`;
    }

    if (path.length > 1 && path.endsWith('/')) {
        path = path.slice(0, -1);
    }

    if (unresolvedRequired) {
        return '#';
    }

    const query = {};
    if (isObjectParams) {
        Object.entries(namedParams).forEach(([key, value]) => {
            if (!usedNamed.has(key)) {
                query[key] = value;
            }
        });
    }

    const queryString = toQueryString(query);
    return queryString ? `${path}?${queryString}` : path;
};

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const pinia = createPinia();

        window.__routeManifest = props?.initialPage?.props?.route_manifest || {};

        inertiaRouter.on('success', (event) => {
            const nextManifest = event?.detail?.page?.props?.route_manifest;
            if (nextManifest && typeof nextManifest === 'object') {
                window.__routeManifest = nextManifest;
            }
        });

        const app = createApp({ render: () => h(App, props) });
        app.config.globalProperties.route = window.route;

        app.config.errorHandler = (err, instance, info) => {
            console.error('VueError:', err, info, instance);
        };

        return app
            .use(plugin)
            .use(pinia)
            .mount(el);
    },
    progress: {
        color: '#4B5563',
    },
});
